<template>
  <Transition name="bottom-bar-fade">
    <div
      v-if="visible"
      class="bottom-pagination-bar"
      :role="isResultsOnlyMode ? 'status' : 'region'"
      :aria-label="isResultsOnlyMode ? 'Results count' : 'Table pagination'"
    >
      <!-- Results-only mode: fixed bar with count on the left (e.g. infinite scroll) -->
      <div
        v-if="isResultsOnlyMode"
        class="bottom-results-bar bg-surface border-t border-border h-[53px] flex items-center justify-start"
        :class="layoutCollapsedStyle?.value ? 'px-4' : 'px-4 sm:px-6 lg:px-8'"
      >
        <span class="text-sm text-text-muted">
          {{ resultsCountText }}
        </span>
      </div>
      <!-- Pagination mode: page number controls -->
      <StickyPaginationBar
        v-else
        :current-page="currentPage"
        :total-pages="totalPages"
        :total-results="totalResults"
        :items-per-page="itemsPerPage"
        :disabled="disabled"
        :max-visible-buttons="maxVisibleButtons"
        @page-changed="handlePageChange"
      />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import StickyPaginationBar from '@/components/navigation/StickyPaginationBar.vue'
import { useBottomPagination } from '@/composables/useBottomPagination'

const sidebarCollapse = inject<{
  isCollapsed: { value: boolean }
  toggle: () => void
  showToggle: { value: boolean }
} | null>('sidebarCollapse', null)

const layoutCollapsedStyle = inject<{ value: boolean } | null>('layoutCollapsedStyle', null)
const isSidebarCollapsed = computed(() => sidebarCollapse?.isCollapsed?.value ?? false)

const {
  visible,
  currentPage,
  totalPages,
  totalResults,
  itemsPerPage,
  disabled,
  maxVisibleButtons,
  handlePageChange,
  isResultsOnlyMode,
  resultsShown,
  resultsTotal,
  resultsLabel
} = useBottomPagination()

const resultsCountText = computed(() => {
  const shown = resultsShown.value
  const total = resultsTotal.value
  const label = resultsLabel.value
  if (typeof total === 'number' && total >= 0) {
    return `Showing ${shown} of ${total.toLocaleString()} ${label}`
  }
  return `Showing ${shown} ${label}`
})
</script>

<style scoped>
.bottom-pagination-bar {
  position: fixed;
  bottom: 0;
  left: var(--bottom-bar-inset-left, 0);
  right: 0;
  z-index: 20;
  transition: left 0.15s ease-out;
}

.bottom-bar-fade-enter-active,
.bottom-bar-fade-leave-active {
  transition: opacity 0.2s ease;
}
.bottom-bar-fade-enter-from,
.bottom-bar-fade-leave-to {
  opacity: 0;
}
</style>
